import { ErrorCodes } from 'app/shared/errors.enums';
import {
  DEFAULT_EXPIRES_IN_SECONDS,
  DEFAULT_RESEND_AVAILABLE_IN_SECONDS,
  FlowStatus,
  MfaChallenge,
  MfaVerifyResult,
  PasswordStepResult,
  RecoveryDescriptor,
  SessionTokens,
} from './auth-flow.model';

const number = (value: any, fallback: number): number => (typeof value === 'number' ? value : fallback);

export function toSessionTokens(raw: any): SessionTokens | null {
  const source = raw?.login_tokens ?? raw;
  const token = source?.token;
  const refreshToken = source?.refreshToken ?? source?.refresh_token;
  if (!token || !refreshToken) return null;

  return {
    token,
    refreshToken,
    token_id: source.token_id ?? null,
    profile_completeness_show_modal: !!source.profile_completeness_show_modal,
    profile_completeness: source.profile_completeness ?? null,
    sso_provider: raw?.sso_provider ?? source.sso_provider ?? null,
    sso_connection: raw?.sso_connection ?? source.sso_connection ?? null,
  };
}

export function toMfaChallenge(raw: any): MfaChallenge | null {
  const source = raw?.challenge ?? raw?.mfa ?? raw;
  const challengeId = source?.challenge_id ?? source?.challengeId;
  if (!challengeId) return null;

  return {
    challengeId,
    factorType: source.factor_type ?? null,
    expiresInSeconds: number(source.expires_in_seconds, DEFAULT_EXPIRES_IN_SECONDS),
    remainingAttempts: number(source.remaining_attempts, Infinity),
    remainingResends: number(source.remaining_resends, Infinity),
    resendAvailableInSeconds: number(source.resend_available_in_seconds, DEFAULT_RESEND_AVAILABLE_IN_SECONDS),
  };
}

export function toRecovery(raw: any): RecoveryDescriptor | null {
  const source = raw?.recovery ?? raw;
  const recoveryToken = source?.recoveryToken ?? source?.recovery_token;
  if (!recoveryToken) return null;

  return { recoveryToken, recoveryType: source.recovery_type ?? null };
}

export function toPasswordStepResult(raw: any): PasswordStepResult {
  const recovery = toRecovery(raw);
  const challenge = toMfaChallenge(raw);
  const tokens = toSessionTokens(raw);

  const declared: FlowStatus | undefined = raw?.flow_status;
  const status: FlowStatus =
    declared ?? (recovery ? 'RECOVERY_REQUIRED' : challenge ? 'MFA_VERIFICATION_REQUIRED' : 'AUTHENTICATED');

  return { status, tokens, challenge, recovery };
}

export function toMfaVerifyResult(raw: any): MfaVerifyResult {
  return {
    tokens: toSessionTokens(raw),
    trustedDeviceToken: raw?.trusted_device_token ?? null,
    trustedDeviceExpiresAt: raw?.trusted_device_expires_at ?? null,
  };
}

export function isPasswordExpired(recovery: RecoveryDescriptor | null, raw: any): boolean {
  if (recovery?.recoveryType === 'PASSWORD_EXPIRED') return true;

  const expired = String(ErrorCodes.PASSWORD_EXPIRED);
  const source = raw?.recovery ?? raw;
  return (
    source?.error_code === ErrorCodes.PASSWORD_EXPIRED ||
    source?.code === ErrorCodes.PASSWORD_EXPIRED ||
    !!source?.details?.includes(expired)
  );
}
